import { useNovelCustomerPassSelector } from "@customer-pass/redux/reduxHooks";
import * as Sentry from "@sentry/browser";
import React, { ReactNode, useCallback, useEffect, useMemo } from "react";
import { useInitAndroidEffect } from "@customer-pass/redux/actionCreators/androidDevice";
import { loadNovelIntercom } from "@novel/shared/utils/loadNovelIntercom";
import { useWalletPassInstallFlow } from "@customer-pass/redux/reducers/walletPassInstallFlow";
import { openInNewTab } from "@novel/shared/utils/locationUtils";
import { appPrefixedHostWithProtocol, vendorPortalPrefix } from "@novel/shared/utils/appConstants";
import { IOrgHandle } from "@novel/shared/interfaces/OrgHandle";
interface Props {
  readonly children?: ReactNode;
  readonly isSuperUserRoute?: boolean;
  readonly isPublicRoute?: boolean;
  readonly isLoggedOutRoute?: boolean;
}
export function InitContainer({
  children
}: Props): JSX.Element {
  const walletPassInstallFlow = useWalletPassInstallFlow();
  const resolvedOrg = useNovelCustomerPassSelector(state => state.orgData.resolvedOrg);
  const resolvedCustomer = useNovelCustomerPassSelector(state => state.auth.resolvedCustomer);
  useInitAndroidEffect();
  useEffect(() => {
    loadNovelIntercom({
      email: resolvedCustomer?.novelUser.primaryEmail,
      phoneNumber: resolvedCustomer?.novelUser.mostRecentPhoneNumber,
      firstName: resolvedCustomer?.novelUser.firstName,
      lastName: resolvedCustomer?.novelUser.lastName,
      walletPassInstallFlow,
      orgName: resolvedOrg?.org.orgName,
      domain: resolvedOrg?.shopData?.domain || resolvedOrg?.org.orgDomain,
      myShopifyDomain: resolvedOrg?.shopData?.myShopifyDomain,
      orgCustomerEmail: resolvedOrg?.shopData?.customer_email,
      orgPartnerEmail: resolvedOrg?.shopData?.email,
      shopOwner: resolvedOrg?.shopData?.shop_owner
    });
  }, [resolvedCustomer?.novelUser, resolvedOrg?.org, resolvedOrg?.shopData, walletPassInstallFlow]);
  useEffect(() => {
    // if (!resolvedCustomer?.novelUser.isSuperUser) {
    //     if (resolvedCustomer?.novelUser && resolvedCustomer?.orgCustomer) {
    //         const { firstName, lastName, displayName, primaryEmail, ...otherFields } =
    //             resolvedCustomer.novelUser;

    //         setUpFullStory("pass", resolvedCustomer.novelUser.id, {
    //             displayName:
    //                 displayName ||
    //                 [firstName, lastName].filter(Boolean).join(" ") ||
    //                 primaryEmail,
    //             email: primaryEmail,
    //             ...otherFields,
    //             ...resolvedCustomer.orgCustomer,
    //         });
    //     } else {
    //         setUpFullStory("pass");
    //     }
    // }

    if (resolvedCustomer) {
      // If user is valid we should track him using this id and email, to track specific issues
      Sentry.setUser({
        id: resolvedCustomer.novelUser.id,
        email: resolvedCustomer.novelUser.primaryEmail
      });
    } else {
      // If user is not valid, we should remove any user information
      Sentry.configureScope(scope => scope.setUser(null));
    }
  }, [resolvedCustomer]);
  return <React.Fragment>
            {children}
            <DemoInstallButton />
        </React.Fragment>;
}
function DemoInstallButton() {
  const walletPassInstallFlow = useWalletPassInstallFlow();
  const resolvedOrg = useNovelCustomerPassSelector(state => state.orgData.resolvedOrg);
  const resolvedCustomer = useNovelCustomerPassSelector(state => state.auth.resolvedCustomer);
  const myShopifyHandles = useMemo(() => resolvedOrg?.myShopifyHandles?.filter(handle => handle.baseHost) || [], [resolvedOrg]);
  const openInstallLink = useCallback((handle: IOrgHandle) => {
    if (handle?.baseHost) {
      // screwed up computing baseHost for a while - fixed now, but this is a fallback
      const correctedBaseHost = handle.baseHost.includes("myshopify") ? handle.baseHost : `${handle.baseHost}.myshopify.com`;
      openInNewTab(`${appPrefixedHostWithProtocol}${vendorPortalPrefix}?shop=${correctedBaseHost}`);
    }
  }, []);
  const demoInstallButtonClick = useCallback(() => {
    openInstallLink(myShopifyHandles[0]);
  }, [myShopifyHandles, openInstallLink]);

  // only show on demo shops on desktop after they've logged in
  if (walletPassInstallFlow !== "large-device" || !resolvedCustomer || !resolvedOrg?.org || resolvedOrg?.shopData || !myShopifyHandles.length) {
    return null;
  }
  return <React.Fragment>
            <div className="demo-install-container">
                <div className="demo-install-description">
                    <span style={{
          marginBottom: 1
        }}>
                        This demo pass is made from your site's assets!
                    </span>
                    <span>Ready to make it yours?</span>
                </div>
                <div className="demo-install-button-wrapper">
                    <button className="button-big demo-install-button" onClick={demoInstallButtonClick}>
                        Start Your 30-day Trial
                    </button>
                </div>
            </div>
            <style>{`
                .demo-install-container {
                    position: fixed;
                    display: inline-flex;
                    flex-direction: column;
                    bottom: 0;
                    left: 50%;
                    transform: translateX(-50%);
                    z-index: 1051;
                    bottom: 20px;
                }

                .demo-install-description {
                    display: flex;
                    flex-direction: column;
                    min-width: 470px;
                    margin-bottom: 8px;

                    text-align: center;
                    color: #fff;
                }

                .demo-install-button-wrapper {
                    margin: auto;
                }

                .demo-install-button {
                    box-shadow: 0 0 10px -1px #fff !important;
                }

                @media (max-width: 768px) {
                    .demo-install-container {
                        display: none;
                        bottom: 6px;
                    }
                }

                @media (max-height: 810px) {
                    .demo-install-description {
                        display: none;
                    }
                }
            `}</style>
        </React.Fragment>;
}